import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Dropdown, Empty, Spin, Typography } from 'antd';
import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import type {
  AppNotification,
  WorkspaceInvitationNotificationData,
} from '@core/entities/AppNotification';
import { useAcceptInvitation, useDeclineInvitation } from '@presentation/hooks/useInvitations';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@presentation/hooks/useNotifications';
import { useAppMessage } from '@presentation/hooks/useAppMessage';
import { paths } from '@app/routes/paths';

const isWorkspaceInvitation = (
  notification: AppNotification,
): notification is AppNotification & { data: WorkspaceInvitationNotificationData } =>
  notification.type === 'workspace_invitation';

const InvitationNotificationItem = ({
  notification,
  onSettled,
}: {
  notification: AppNotification & { data: WorkspaceInvitationNotificationData };
  onSettled: () => void;
}) => {
  const navigate = useNavigate();
  const message = useAppMessage();
  const acceptInvitation = useAcceptInvitation();
  const declineInvitation = useDeclineInvitation();
  const markRead = useMarkNotificationRead();
  const isPending = acceptInvitation.isPending || declineInvitation.isPending;

  const handleAccept = () => {
    acceptInvitation.mutate(notification.data.invitation_token, {
      onSuccess: () => {
        markRead.mutate(notification.id);
        void message.success(`Joined "${notification.data.workspace_name}".`);
        navigate(paths.workspaces);
        onSettled();
      },
      onError: (error) =>
        void message.error(error.message || 'This invitation is no longer valid.'),
    });
  };

  const handleDecline = () => {
    declineInvitation.mutate(notification.data.invitation_token, {
      onSuccess: () => {
        markRead.mutate(notification.id);
        onSettled();
      },
      onError: (error) =>
        void message.error(error.message || 'This invitation is no longer valid.'),
    });
  };

  return (
    <div className={`flex flex-col gap-2 p-3 ${notification.readAt ? 'opacity-60' : ''}`}>
      <Typography.Text className="text-sm">
        <strong>{notification.data.inviter_name}</strong> invited you to{' '}
        <strong>{notification.data.workspace_name}</strong>
      </Typography.Text>
      <div className="flex gap-2">
        <Button
          type="primary"
          size="small"
          loading={acceptInvitation.isPending}
          disabled={isPending}
          onClick={handleAccept}
        >
          Accept
        </Button>
        <Button
          size="small"
          loading={declineInvitation.isPending}
          disabled={isPending}
          onClick={handleDecline}
        >
          Decline
        </Button>
      </div>
    </div>
  );
};

const NotificationList = ({ onItemSettled }: { onItemSettled: () => void }) => {
  const { data: notifications, isLoading } = useNotifications();
  const markAllRead = useMarkAllNotificationsRead();
  const unreadCount = notifications?.filter((notification) => !notification.readAt).length ?? 0;

  if (isLoading) {
    return (
      <div className="flex w-80 justify-center p-6">
        <Spin size="small" />
      </div>
    );
  }

  return (
    <div className="w-80">
      <div className="flex items-center justify-between border-b border-black/10 px-3 py-2 dark:border-white/10">
        <Typography.Text strong className="text-sm">
          Notifications
        </Typography.Text>
        <Button
          type="text"
          size="small"
          icon={<CheckOutlined />}
          disabled={unreadCount === 0}
          loading={markAllRead.isPending}
          onClick={() => markAllRead.mutate()}
        >
          Mark all read
        </Button>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {!notifications || notifications.length === 0 ? (
          <Empty
            className="py-6"
            description="No notifications yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <div className="divide-y divide-black/5 dark:divide-white/10">
            {notifications.map((notification) =>
              isWorkspaceInvitation(notification) ? (
                <InvitationNotificationItem
                  key={notification.id}
                  notification={notification}
                  onSettled={onItemSettled}
                />
              ) : (
                <div key={notification.id} className="p-3 text-sm text-gray-500">
                  Unsupported notification type.
                </div>
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const { data: notifications } = useNotifications();
  const unreadCount = notifications?.filter((notification) => !notification.readAt).length ?? 0;

  return (
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      popupRender={() => <NotificationList onItemSettled={() => setOpen(false)} />}
    >
      <Badge count={unreadCount} size="small" overflowCount={99} offset={[-4, 2]}>
        <Button type="text" icon={<BellOutlined />} aria-label="Notifications" />
      </Badge>
    </Dropdown>
  );
};
